import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { Workflow, WorkflowConfigError } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import {
  WorkflowStep,
  WorkflowStepInput,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import Action from 'lib/interfaces/Action';

export enum WorkflowActionType {
  SetWorkflow = 'workflow_v2/SET_WORKFLOW',
  SetWorkflowVersion = 'workflow_v2/SET_WORKFLOW_VERSION',
  SetPublishedWorkflowVersion = 'workflow_v2/SET_PUBLISHED_WORKFLOW_VERSION',
  SetWorkflowVersionErrors = 'workflow_v2/SET_ERRORS',
  Undo = 'workflow_v2/UNDO',
  Redo = 'workflow_v2/REDO',
  ClearHistory = 'workflow_v2/CLEAR_HISTORY',
  SetWorkflowDeployment = 'workflow_v2/SET_WORKFLOW_DEPLOYMENT',
  SetInputStep = 'workflow_v2/SET_INPUT_STEP',
  SetStepContexts = 'workflow_v2/SET_STEP_CONTEXTS',
  SetStepsConfig = 'workflow_v2/SET_STEPS_CONFIG',
}

export interface WorkflowPayload {
  workflow: Workflow;
}

export interface WorkflowVersionErrorsPayload {
  errors: WorkflowConfigError[];
}

export interface WorkflowVersionPayload {
  workflowVersion: WorkflowVersion;
}

export interface WorkflowDeploymentPayload {
  workflowDeployment: WorkflowDeployment | null;
}

export interface InputStepPayload {
  inputStep: WorkflowStepInput;
}

export interface StepContextsPayload {
  stepContexts: Record<string, UsableObjectConfig[]>;
}

export interface StepsConfigPayload {
  stepsConfig: WorkflowStep[];
}

export function setWorkflow(
  workflow: WorkflowPayload['workflow'],
): Action<WorkflowActionType.SetWorkflow, WorkflowPayload> {
  return {
    payload: {
      workflow,
    },
    type: WorkflowActionType.SetWorkflow,
  };
}

export function setWorkflowVersion(
  workflowVersion: WorkflowVersionPayload['workflowVersion'],
): Action<WorkflowActionType.SetWorkflowVersion, WorkflowVersionPayload> {
  return {
    payload: {
      workflowVersion,
    },
    type: WorkflowActionType.SetWorkflowVersion,
  };
}

export function setPublishedWorkflowVersion(
  workflowVersion: WorkflowVersionPayload['workflowVersion'],
): Action<WorkflowActionType.SetPublishedWorkflowVersion, WorkflowVersionPayload> {
  return {
    payload: {
      workflowVersion,
    },
    type: WorkflowActionType.SetPublishedWorkflowVersion,
  };
}

export function setWorkflowDeployment(
  workflowDeployment: WorkflowDeploymentPayload['workflowDeployment'],
): Action<WorkflowActionType.SetWorkflowDeployment, WorkflowDeploymentPayload> {
  return {
    payload: {
      workflowDeployment,
    },
    type: WorkflowActionType.SetWorkflowDeployment,
  };
}

export function setInputStep(
  inputStep: InputStepPayload['inputStep'],
): Action<WorkflowActionType.SetInputStep, InputStepPayload> {
  return {
    payload: {
      inputStep,
    },
    type: WorkflowActionType.SetInputStep,
  };
}

export function setWorkflowVersionErrors(
  errors: WorkflowVersionErrorsPayload['errors'],
): Action<WorkflowActionType.SetWorkflowVersionErrors, WorkflowVersionErrorsPayload> {
  return {
    payload: { errors },
    type: WorkflowActionType.SetWorkflowVersionErrors,
  };
}

export function setStepsConfig(
  stepsConfig: StepsConfigPayload['stepsConfig'],
): Action<WorkflowActionType.SetStepsConfig, StepsConfigPayload> {
  return {
    payload: { stepsConfig },
    type: WorkflowActionType.SetStepsConfig,
  };
}

export function setStepContexts(
  stepContexts: StepContextsPayload['stepContexts'],
): Action<WorkflowActionType.SetStepContexts, StepContextsPayload> {
  return {
    payload: { stepContexts },
    type: WorkflowActionType.SetStepContexts,
  };
}

export function undoWorkflow(): Action<WorkflowActionType.Undo> {
  return {
    type: WorkflowActionType.Undo,
  };
}

export function redoWorkflow(): Action<WorkflowActionType.Redo> {
  return {
    type: WorkflowActionType.Redo,
  };
}

export function clearWorkflowHistory(): Action<WorkflowActionType.ClearHistory> {
  return {
    type: WorkflowActionType.ClearHistory,
  };
}

export type WorkflowActions =
  | Action<WorkflowActionType.SetWorkflow, WorkflowPayload>
  | Action<WorkflowActionType.Undo, never>
  | Action<WorkflowActionType.Redo, never>
  | Action<WorkflowActionType.ClearHistory, never>
  | Action<WorkflowActionType.SetWorkflowVersion, WorkflowVersionPayload>
  | Action<WorkflowActionType.SetPublishedWorkflowVersion, WorkflowVersionPayload>
  | Action<WorkflowActionType.SetWorkflowVersionErrors, WorkflowVersionErrorsPayload>
  | Action<WorkflowActionType.SetWorkflowDeployment, WorkflowDeploymentPayload>
  | Action<WorkflowActionType.SetInputStep, InputStepPayload>
  | Action<WorkflowActionType.SetStepsConfig, StepsConfigPayload>
  | Action<WorkflowActionType.SetStepContexts, StepContextsPayload>;
